import { isWall } from './freeFor.js';
import type { GroupMode } from './grouping.js';
import { type DisplayState, WORD, displayStateOf } from './timeline/barStyle.js';
import type { ScheduleView } from './timeline/dropLogic.js';
import type { ScheduleBar } from './types.js';

/**
 * 316 (US6, FR-012) — everything the schedule is showing lives in the address, so a reload or a
 * shared link opens the same view, date and filters. Only what differs from a default is written.
 */

/** The booking statuses the status filter offers — named exactly as the bars and legend name them. */
export const STATUS_FILTER_VALUES = [
  'awaiting_payment',
  'confirmed',
  'checked_out',
  'checked_in',
  'likely_abandoned',
  'no_hold',
] as const satisfies readonly DisplayState[];

export type StatusFilter = (typeof STATUS_FILTER_VALUES)[number];

export const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> =
  STATUS_FILTER_VALUES.map((value) => ({ value, label: WORD[value] }));

export interface ScheduleParams {
  view: ScheduleView;
  /** The anchor date (`YYYY-MM-DD`), or null to let the platform pick today in its own zone. */
  date: string | null;
  plate: string;
  statuses: StatusFilter[];
  location: string | null;
  unavailable: boolean;
  /** 319 (FR-002): rows grouped by location or category; in the address, like the filters. 384: Location by default. */
  group: GroupMode;
  /** 320 (FR-006): the free-for window, Malaysia wall clock (`YYYY-MM-DDTHH:mm`); both or neither. */
  freeFrom: string | null;
  freeTo: string | null;
}

const isStatus = (s: string): s is StatusFilter =>
  (STATUS_FILTER_VALUES as readonly string[]).includes(s);

export function readScheduleParams(q: URLSearchParams): ScheduleParams {
  const view = q.get('view');
  const date = q.get('date');
  const freeFrom = q.get('free_from');
  const freeTo = q.get('free_to');
  const freeOk = !!freeFrom && !!freeTo && isWall(freeFrom) && isWall(freeTo);
  return {
    view: view === 'week' || view === 'month' ? view : 'day',
    date: date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null,
    plate: q.get('plate') ?? '',
    statuses: (q.get('status') ?? '').split(',').filter(isStatus),
    location: q.get('location') || null,
    unavailable: q.get('unavailable') === '1',
    // 384 (#1276): Location is the default; "no grouping" is a choice the address keeps as `group=none`.
    group:
      // 396: `group=host` too.
      q.get('group') === 'none' || q.get('group') === 'category' || q.get('group') === 'host'
        ? (q.get('group') as GroupMode)
        : 'location',
    freeFrom: freeOk ? freeFrom : null,
    freeTo: freeOk ? freeTo : null,
  };
}

export function writeScheduleParams(p: ScheduleParams): URLSearchParams {
  const q = new URLSearchParams();
  if (p.view !== 'day') q.set('view', p.view);
  if (p.date) q.set('date', p.date);
  if (p.plate) q.set('plate', p.plate);
  if (p.statuses.length > 0) q.set('status', p.statuses.join(','));
  if (p.location) q.set('location', p.location);
  if (p.unavailable) q.set('unavailable', '1');
  if (p.group !== 'location') q.set('group', p.group);
  if (p.freeFrom && p.freeTo) {
    q.set('free_from', p.freeFrom);
    q.set('free_to', p.freeTo);
  }
  return q;
}

/**
 * The status filter: bookings in the chosen statuses (by their REAL status, as the bars show it),
 * plus every block — a block is not a booking, and hiding a maintenance window because the operator
 * asked about checked-out rentals would hide why a car is unavailable.
 */
export function filterBars(bars: ScheduleBar[], statuses: readonly StatusFilter[]): ScheduleBar[] {
  if (statuses.length === 0) return bars;
  return bars.filter((b) => {
    if (b.kind !== 'booking' && b.kind !== 'extension') return true;
    return (statuses as readonly string[]).includes(displayStateOf(b));
  });
}

/**
 * 301's rule, kept (FR-022): after moving the car at `from` to `to`, report it and the car it now
 * follows — null when it is first.
 */
export function reorderAfter(
  ids: readonly string[],
  from: number,
  to: number,
): { carId: string; afterCarId: string | null } | null {
  if (from === to || from < 0 || to < 0 || from >= ids.length || to >= ids.length) return null;
  const next = [...ids];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return null;
  next.splice(to, 0, moved);
  return { carId: moved, afterCarId: to === 0 ? null : (next[to - 1] ?? null) };
}
